import { Grid, Stack, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { PersistedTabPanel } from "../../../../../../../../common/PersistedTabPanel";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type {
  CellDrillDownInfo,
  CellDrillDownInfoTypes,
  ChartOfAccountsDataItem,
  ChartOfAccountsLedgerItem,
  DrillDownInfo,
} from "../../../../../../../../../api/types/svTypes.ts";
import { CellDrillDownInfoType, MeasureUnitTable } from "../../../../../../../../../api/types/svTypes.ts";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";
import { createDrillDownPageAction } from "../../../../../store/utils/storeUtils";
import { checkUnitTable } from "../../../../../utilities/checkUnitTable.ts";
import { useChartOfAccountsContext } from "../../chartOfAccounts/contexts/ChartOfAccountsContext";
import { drillDownTabSx, drillDownTabsSx } from "../constants";
import { isMeasureDrillDownInfo } from "../isMeasureDrillDownInfo";
import LedgerAccountsList from "./LedgerAccountsList";

interface Props {
  configuration: DrillDownPage;
  info: DrillDownInfo;
  chartOfAccounts: ChartOfAccountsLedgerItem[];
}

export default function ChartOfAccountsListContainer({ configuration, info, chartOfAccounts }: Props) {
  const dispatch = useDispatch();
  const { selectedAccounts, scrollTargetAccount } = useChartOfAccountsContext();
  const glAccounts = chartOfAccounts.find((coa) => checkUnitTable(coa.measureUnitTable, MeasureUnitTable.Gl));
  const memoAccounts = chartOfAccounts.find((coa) => checkUnitTable(coa.measureUnitTable, MeasureUnitTable.Memo));

  const hasGl = glAccounts && glAccounts.items.length > 0;
  const hasMemo = memoAccounts && memoAccounts.items.length > 0;

  const glInfo = info.items.find(
    (item) => isMeasureDrillDownInfo(item) && checkUnitTable(item.table, MeasureUnitTable.Gl)
  );
  const memoInfo = info.items.find(
    (item) => isMeasureDrillDownInfo(item) && checkUnitTable(item.table, MeasureUnitTable.Memo)
  );

  const [selectedTab, setSelectedTab] = useState<MeasureUnitTable>(hasGl ? MeasureUnitTable.Gl : MeasureUnitTable.Memo);
  useEffect(() => {
    if (selectedAccounts.length > 0) {
      const isGl = chartOfAccounts
        .find((coa) => checkUnitTable(coa.measureUnitTable, MeasureUnitTable.Gl))
        ?.items.some((account) => selectedAccounts.includes(account.accountNo));
      if (isGl) {
        // TODO #209
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(MeasureUnitTable.Gl);
        return;
      }
      const isMemo = chartOfAccounts
        .find((coa) => checkUnitTable(coa.measureUnitTable, MeasureUnitTable.Memo))
        ?.items.some((account) => selectedAccounts.includes(account.accountNo));
      if (isMemo) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(MeasureUnitTable.Memo);
      }
    }
  }, [chartOfAccounts, selectedAccounts]);

  useEffect(() => {
    if (scrollTargetAccount?.accountNo) {
      const isGl = chartOfAccounts
        .find((coa) => checkUnitTable(coa.measureUnitTable, MeasureUnitTable.Gl))
        ?.items.some((account) => scrollTargetAccount.accountNo === account.accountNo);
      if (isGl) {
        // TODO #209
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(MeasureUnitTable.Gl);
        return;
      }
      const isMemo = chartOfAccounts
        .find((coa) => checkUnitTable(coa.measureUnitTable, MeasureUnitTable.Memo))
        ?.items.some((account) => scrollTargetAccount.accountNo === account.accountNo);
      if (isMemo) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(MeasureUnitTable.Memo);
      }
    }
  }, [chartOfAccounts, scrollTargetAccount?.accountNo]);

  const handleSubDrillDown = useCallback(
    (itemInfo: CellDrillDownInfoTypes | undefined, row: ChartOfAccountsDataItem) => {
      const newConfiguration = createSubDrillDown(row, configuration, itemInfo || info.combined);
      if (newConfiguration !== undefined) dispatch(createDrillDownPageAction("add", newConfiguration));
    },
    [configuration, info.combined, dispatch]
  );

  return (
    <Stack spacing={2} useFlexGap sx={{ py: 2.5, pr: 3, pl: 1.5, width: "100%" }}>
      <Tabs value={selectedTab} sx={drillDownTabsSx} onChange={(_, value: MeasureUnitTable) => setSelectedTab(value)}>
        {hasGl && (
          <Tab
            key={MeasureUnitTable.Gl}
            label="General Ledger Accounts"
            value={MeasureUnitTable.Gl}
            sx={drillDownTabSx}
          />
        )}
        {hasMemo && (
          <Tab
            key={MeasureUnitTable.Memo}
            label="Memo Ledger Accounts"
            value={MeasureUnitTable.Memo}
            sx={drillDownTabSx}
          />
        )}
      </Tabs>
      <Grid container sx={{ flex: 1, width: "100%" }}>
        <PersistedTabPanel key={MeasureUnitTable.Gl} value={MeasureUnitTable.Gl} selectedTab={selectedTab}>
          <LedgerAccountsList
            configuration={configuration}
            info={glInfo}
            items={glAccounts?.items || []}
            ledger={MeasureUnitTable.Gl}
            onDrillDown={handleSubDrillDown}
          />
        </PersistedTabPanel>
        <PersistedTabPanel key={MeasureUnitTable.Memo} value={MeasureUnitTable.Memo} selectedTab={selectedTab}>
          <LedgerAccountsList
            configuration={configuration}
            info={memoInfo}
            items={memoAccounts?.items || []}
            ledger={MeasureUnitTable.Memo}
            onDrillDown={handleSubDrillDown}
          />
        </PersistedTabPanel>
      </Grid>
    </Stack>
  );
}

function createSubDrillDown(row: ChartOfAccountsDataItem, configuration: DrillDownPage, info: CellDrillDownInfoTypes) {
  const measure = getSubDrillDownMeasure(configuration, row);

  const newConfiguration: DrillDownPage = {
    pageId: generateGuid(),
    reportId: configuration.reportId,
    onlyLedger: true,
    chartOfAccounts: false,
    layer: {
      measure,
      cell: {
        value: row.amount,
        formattedValue: row.amountFormatted || "",
        drillDownId: undefined,
      },
      info: {
        combined: {
          ...info,
          drillDownInfoType: CellDrillDownInfoType.Default,
          conditions: row.conditions,
        } as CellDrillDownInfo,
        items: [],
      },
    },
  };

  return newConfiguration;
}

function getSubDrillDownMeasure(configuration: DrillDownPage, row: ChartOfAccountsDataItem) {
  const measure = structuredClone(getActiveLayer(configuration).measure);
  if (measure !== undefined) {
    measure.config.customLabel = row.accountName;
  }

  return measure;
}
